import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);

  if (!article) {
    return (
      <SiteLayout>
        <PageMeta
          title="笔记未找到｜511 求证档案"
          description="没有找到对应的求证笔记，可能链接已变更。"
          keywords="511求证档案,求证笔记"
          path="/articles"
        />
        <section className="flex w-full flex-col items-center px-4 py-20 text-center md:px-6 lg:px-10">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-file-unknow-line text-2xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            没有找到这篇求证笔记
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            它可能已被合并到其他条目。回到笔记列表看看其他内容。
          </p>
          <Link
            to="/articles"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回求证笔记
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const related = articles
    .filter((item) => item.id !== article.id)
    .sort((a, b) => (a.category === article.category ? -1 : 1))
    .slice(0, 3);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: article.author },
      keywords: article.tags.join(", "),
      articleSection: article.category,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://balha.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "求证笔记",
          item: "https://balha.cn/articles",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `https://balha.cn/articles/${article.id}`,
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <PageMeta
        title={`${article.title}｜511 求证笔记`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},511爆料,511爆料细节补充`}
        path={`/articles/${article.id}`}
        ogType="article"
        schema={schema}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="paper-grid w-full">
          <div className="w-full px-4 py-10 md:px-6 md:py-14 lg:px-10">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "求证笔记", to: "/articles" },
                { label: article.title },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className="whitespace-nowrap rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-900">
                {article.category}
              </span>
              <span className="whitespace-nowrap">{article.date}</span>
              <span className="whitespace-nowrap">{article.readingTime}</span>
              <span className="whitespace-nowrap">作者：{article.author}</span>
            </div>
            <h1 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-snug tracking-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {article.excerpt}
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-6 md:py-14 lg:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          <article className="lg:col-span-2">
            <div className="h-[220px] w-full overflow-hidden rounded-lg border border-background-300 bg-background-200 md:h-[340px]">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title}｜511爆料求证笔记`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 flex flex-col gap-8">
              {article.sections.map((section, index) => (
                <section key={section.heading} id={`sec-${index}`}>
                  <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                    <span className="mr-2 text-primary-600">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    {section.heading}
                  </h2>
                  <div className="mt-3 flex flex-col gap-3">
                    {section.paragraphs.map((paragraph) => (
                      <p
                        key={paragraph}
                        className="text-sm leading-loose text-foreground-700 md:text-base"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  {section.points && (
                    <ul className="mt-4 flex flex-col gap-2 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
                      {section.points.map((point) => (
                        <li
                          key={point}
                          className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700"
                        >
                          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-600">
                            <i className="ri-checkbox-circle-line text-base"></i>
                          </span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200 pt-6">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          <aside className="flex flex-col gap-5">
            <nav
              aria-label="文章目录"
              className="rounded-lg border border-background-200 bg-background-100 p-5"
            >
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                本篇目录
              </h2>
              <ol className="mt-4 flex flex-col gap-2.5">
                {article.sections.map((section, index) => (
                  <li key={section.heading}>
                    <a
                      href={`#sec-${index}`}
                      className="flex items-start gap-2 text-sm leading-snug text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      <span className="font-heading text-xs text-primary-600">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {section.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="rounded-lg border border-primary-200 bg-primary-100 p-5">
              <h2 className="font-heading text-base font-semibold text-primary-800">
                有补充材料？
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-primary-800/90">
                如果你手里有与本文相关的更早来源或原始文件，欢迎提交，我们会更新对应条目。
              </p>
              <Link
                to="/about#tips"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                提交线索
              </Link>
            </div>
          </aside>
        </div>

        <div className="mt-12 border-t border-background-200 pt-10">
          <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
            相关求证笔记
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}